import type { ChangeEvent, FC } from 'react'
import { useId, useState } from 'react'
import type { SxProps, Theme } from '@mui/material'
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material'

export interface BulkConfirmItem {
  readonly id: string
  readonly displayName: string
}

interface BulkConfirmDialogProps {
  readonly open: boolean
  readonly onClose: () => void
  readonly onConfirm: () => void
  readonly isActioning: boolean
  readonly actionLabel: string
  readonly entityLabel: string
  readonly entityLabelPlural: string
  readonly items: readonly BulkConfirmItem[]
}

export const BulkConfirmDialog: FC<BulkConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  isActioning,
  actionLabel,
  entityLabel,
  entityLabelPlural,
  items,
}) => {
  const titleId = useId()
  const [confirmInput, setConfirmInput] = useState('')

  const count = items.length
  const entityName = count === 1 ? entityLabel : entityLabelPlural
  const requiresTypedConfirmation = count >= TYPED_CONFIRMATION_THRESHOLD
  const isConfirmEnabled = requiresTypedConfirmation ? confirmInput === count.toString() : true

  const handleClose = (): void => {
    onClose()
  }

  const handleConfirm = (): void => {
    onConfirm()
  }

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setConfirmInput(event.target.value)
  }

  const visibleItems = items.slice(0, MAX_VISIBLE_ITEMS)
  const remainingCount = count - visibleItems.length
  const hasInputError = confirmInput.length > 0 && confirmInput !== count.toString()

  // MUI Dialog restores focus to the trigger element by default (disableRestoreFocus=false)
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby={titleId}
      TransitionProps={{
        onExited: () => {
          setConfirmInput('')
        },
      }}
    >
      <DialogTitle id={titleId}>
        {actionLabel} {count} {entityName}?
      </DialogTitle>

      <DialogContent>
        <DialogContentText sx={promptSx}>This will {actionLabel.toLowerCase()}:</DialogContentText>

        <List dense disablePadding>
          {visibleItems.map((item) => (
            <ListItem key={item.id} disableGutters sx={listItemSx}>
              <ListItemText primary={item.displayName} primaryTypographyProps={listItemTextProps} />
            </ListItem>
          ))}
        </List>

        {remainingCount > 0 && (
          <Typography variant="body2" sx={remainingTextSx}>
            ... and {remainingCount} more
          </Typography>
        )}

        {requiresTypedConfirmation && (
          <TextField
            autoFocus
            fullWidth
            size="small"
            margin="dense"
            placeholder={`Type ${count}`}
            value={confirmInput}
            onChange={handleInputChange}
            error={hasInputError}
            helperText={
              hasInputError
                ? 'Number does not match'
                : `Type the number of ${entityLabelPlural.toLowerCase()} (${count}) to confirm`
            }
            inputProps={{
              'aria-label': `Type ${count} to confirm`,
              inputMode: 'numeric' as const,
            }}
            sx={textFieldSx}
          />
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isActioning}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleConfirm}
          disabled={!isConfirmEnabled || isActioning}
          startIcon={isActioning ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {actionLabel} {count} {entityName}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TYPED_CONFIRMATION_THRESHOLD = 10
const MAX_VISIBLE_ITEMS = 5

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const promptSx: SxProps<Theme> = {
  mb: 1,
}

const listItemSx: SxProps<Theme> = {
  pl: 2,
  '&::before': {
    content: String.raw`"\2022"`,
    position: 'absolute',
    left: 0,
    color: 'text.secondary',
  },
}

const listItemTextProps = {
  variant: 'body2' as const,
  sx: { color: 'text.primary' },
}

const remainingTextSx: SxProps<Theme> = {
  color: 'text.secondary',
  fontStyle: 'italic',
  pl: 2,
  mt: 0.5,
}

const textFieldSx: SxProps<Theme> = {
  mt: 2,
}
